import { createWebHistory, createRouter, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { handleDashboardNavigation, handleSettingsNavigation } from '@/routes/guards/navigationGuard.ts';
import { useUserStore } from '@/store/user';
import { getCookie } from '@/helpers/cookies.ts';
import { setToken } from '@/helpers/authUtils.ts';

const AuthLayout = () => import('@/components/Layouts/AuthLayout.vue');
const MainLayout = () => import('@/components/Layouts/MainLayout.vue');

const Login = () => import('@/pages/Login.vue');
const Companies = () => import('@/pages/Companies.vue');
const AccountantUserDashboardPage = () => import('@/pages/AccountantUserDashboardPage.vue');
const AccountantChiefDashboardPage = () => import('@/pages/AccountantChiefDashboardPage.vue');
const SettingsTeamMembersPage = () => import('@/pages/SettingsTeamMembersPage.vue');
const CompanyDashboardPage = () => import('@/pages/CompanyDashboardPage.vue');
const CompanyFolders = () => import('@/pages/CompanyFolders.vue');
const CompanyContractsFiles = () => import('@/pages/CompanyContractsFiles.vue');
const CompanyFolderPeriods = () => import('@/pages/CompanyFolderPeriods.vue');
const CompanySubFoldersPeriod = () => import('@/pages/CompanySubFoldersPeriod.vue');
const CompanySubFolderPeriodFiles = () => import('@/pages/CompanySubFolderPeriodFiles.vue');
const AccountingPeriodTransactionsPage = () => import('@/pages/AccountingPeriodTransactionsPage.vue');
const TicketsAccountingPeriodPage = () => import('@/pages/TicketsAccountingPeriodPage.vue');
const TicketPage = () => import('@/pages/TicketPage.vue');
const Transactions = () => import('@/pages/Transactions.vue');
const CompanyAccountingDocumentSearchPage = () => import('@/pages/CompanyAccountingDocumentSearchPage.vue');
const CompanyAutopilotPage = () => import('@/pages/CompanyAutopilotPage.vue');
const NotFoundPage = () => import('@/pages/NotFound.vue');

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'Login',
        component: Login,
      },
    ],
  },
  {
    path: '/',
    component: MainLayout,
    children: [
      {
        path: '',
        name: 'Companies',
        component: Companies,
        meta: { showFileMoveAbortButton: true },
        beforeEnter: handleDashboardNavigation,
      },
      {
        path: '/accountant-user-dashboard',
        name: 'AccountantUserDashboard',
        component: AccountantUserDashboardPage,
        beforeEnter: handleDashboardNavigation,
      },
      {
        path: '/accountant-chief-dashboard',
        name: 'AccountantChiefDashboard',
        component: AccountantChiefDashboardPage,
        beforeEnter: handleDashboardNavigation,
      },
      {
        path: 'settings',
        name: 'Settings',
        redirect: { name: 'SettingsAccountPage' },
        meta: { showFileMoveAbortButton: true },
        children: [
          {
            path: 'team-members',
            name: 'SettingsTeamMembersPage',
            component: SettingsTeamMembersPage,
            beforeEnter: handleSettingsNavigation,
          },
        ],
      },
      {
        path: 'dashboard/:company_id',
        name: 'CompanyDashboardPage',
        component: CompanyDashboardPage,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
          };
        },
      },
      {
        path: 'cmp/:company_id',
        name: 'Company',
        component: CompanyFolders,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
          };
        },
      },
      {
        path: 'cmp/:company_id/contracts/:contracts_folder_id',
        name: 'CompanyContractsFilesPage',
        component: CompanyContractsFiles,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
            contractsFolderId: route.params.contracts_folder_id,
          };
        },
      },
      {
        path: 'cmp/:company_id/folder/:folder_id',
        name: 'CompanyFolderPeriodsPage',
        component: CompanyFolderPeriods,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
            folderId: route.params.folder_id,
          };
        },
      },
      {
        path: 'cmp/:company_id/folder/:folder_id/period/:period_folder_id',
        name: 'CompanySubFoldersPeriodPage',
        component: CompanySubFoldersPeriod,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
            folderId: route.params.folder_id,
            periodFolderId: route.params.period_folder_id,
          };
        },
      },
      {
        path: 'cmp/:company_id/folder/:folder_id/period/:period_folder_id/sub/:subfolder_id',
        name: 'CompanySubFolderPeriodFilesPage',
        component: CompanySubFolderPeriodFiles,
        meta: { isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
            folderId: route.params.folder_id,
            periodFolderId: route.params.period_folder_id,
            subfolderId: route.params.subfolder_id,
          };
        },
      },
      {
        path: 'cmp/:company_id/folder/:folder_id/period/:period_folder_id/transactions',
        name: 'AccountingPeriodTransactionsPage',
        component: AccountingPeriodTransactionsPage,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
            folderId: route.params.folder_id,
            periodFolderId: route.params.period_folder_id,
          };
        },
      },
      {
        path: 'cmp/:company_id/folder/:folder_id/period/:period_folder_id/questions',
        name: 'TicketsAccountingPeriodPage',
        component: TicketsAccountingPeriodPage,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
            folderId: route.params.folder_id,
            periodFolderId: route.params.period_folder_id,
          };
        },
      },
      {
        path: 'cmp/:company_id/folder/:folder_id/period/:period_folder_id/ticket/:ticket_id',
        name: 'TicketPage',
        component: TicketPage,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
            folderId: route.params.folder_id,
            periodFolderId: route.params.period_folder_id,
            ticketId: route.params.ticket_id,
          };
        },
      },
      {
        path: 'cmp/:company_id/transactions',
        name: 'Transactions',
        component: Transactions,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
          };
        },
      },
      {
        path: 'cmp/:company_id/search',
        name: 'CompanyAccountingDocumentSearchPage',
        component: CompanyAccountingDocumentSearchPage,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
          };
        },
      },
      {
        path: 'cmp/:company_id/autopilot',
        name: 'CompanyAutopilotPage',
        component: CompanyAutopilotPage,
        meta: { showFileMoveAbortButton: true, isCompanyRelated: true },
        props(route: RouteLocationNormalized) {
          return {
            companyId: route.params.company_id,
          };
        },
      },
      {
        path: '/:catchAll(.*)',
        name: 'NotFound',
        component: NotFoundPage,
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  if (to.path.startsWith('/loginby/')) {
    const token = to.path.replace('/loginby/', '');
    setToken(token);
    await useUserStore().loginByToken(token);
    return { name: 'Companies' };
  }

  const token = getCookie('sid');
  if (!token && to.name !== 'Login') {
    return { name: 'Login' };
  }
});

export default router;
